import React, { useState, useEffect, useRef } from 'react';

import { styled } from '@uhg-abyss/web/tools/styled';
import { useSearchParams, useNavigate } from 'react-router';
import ChatWidget from '../ChatWidget';
import ChatWelcomeBanner from '../components/ChatWelcomeBanner';
import OptumusLogo from '../assets/Optumus-logo-transparent.png';
import ChatLoadingScreen from './ChatLoadingScreen';
import { useConsentStatus } from '../hooks/useConsentStatus';
import { useContentConfig, useRedirect, useFeatureFlag } from '../hooks';

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
    __consentGranted?: boolean;
  }
}

export const ChatScreen: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setRedirectUrl } = useRedirect();
  const initialPrompt = searchParams.get('initialPrompt') ?? undefined;
  const [consentChecked, setConsentChecked] = useState(false);
  const [waitingForConsent, setWaitingForConsent] = useState(false);
  const consentRequestedRef = useRef(false);
  const chatInitializedRef = useRef(false);

  // Pin the layout to the visual viewport so the input stays just above the
  // on-screen keyboard. iOS Safari/WebView don't shrink `dvh` when the keyboard
  // opens, and WebKit scrolls the page to reveal the focused field (creating a
  // `visualViewport.offsetTop`). We track both the height and the offset and pin
  // the container via `position: fixed`. Guards keep it blank-proof: we never
  // write a 0/invalid height, and the container falls back to `100dvh`.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }

    const root = document.documentElement;

    // Lock page-level scrolling so only the internal message list scrolls.
    const body = document.body;
    const previousOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    let rafId = 0;

    const applyViewport = () => {
      rafId = 0;
      const appHeight = viewport.height;
      // Guard against WebViews that report a 0/invalid height at first paint.
      // Writing `--app-height: 0px` would collapse the container (the `100dvh`
      // fallback only applies when the var is unset) and show a blank screen.
      if (!Number.isFinite(appHeight) || appHeight <= 0) {
        return;
      }
      const offsetTop = Number.isFinite(viewport.offsetTop) ? viewport.offsetTop : 0;

      // Read both together so the height and offset are always applied in the
      // same frame (avoids a flicker where the footer briefly jumps to the top).
      root.style.setProperty('--app-height', `${appHeight}px`);
      root.style.setProperty('--app-offset-top', `${offsetTop}px`);
    };

    const scheduleUpdate = () => {
      if (rafId) {
        return;
      }
      rafId = window.requestAnimationFrame(applyViewport);
    };

    applyViewport();
    viewport.addEventListener('resize', scheduleUpdate);
    viewport.addEventListener('scroll', scheduleUpdate);

    return () => {
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      body.style.overflow = previousOverflow;
      viewport.removeEventListener('resize', scheduleUpdate);
      viewport.removeEventListener('scroll', scheduleUpdate);
      root.style.removeProperty('--app-height');
      root.style.removeProperty('--app-offset-top');
    };
  }, []);

  // Fetch content config in parallel with the session/consent request so the
  // chat can render as soon as both are ready (single loading screen for both).
  const { loading: isConfigLoading } = useContentConfig();
  const showWelcomeBanner = useFeatureFlag('showWelcomeBanner');

  const { isLoading: isConsentLoading } = useConsentStatus({
    enabled: true,
    onSuccess: (data) => {
      if (data.isConsentActive) {
        // Store the redirect URL for when user closes the chat
        // Priority: URL param > API response (same logic as hs-mp-web)
        const redirectUrlFromParams = searchParams.get('redirectUrl');
        const finalRedirectUrl = redirectUrlFromParams || data.redirectUrl;

        if (finalRedirectUrl) {
          setRedirectUrl(finalRedirectUrl);
        }

        if (!chatInitializedRef.current) {
          chatInitializedRef.current = true;
          setConsentChecked(true);
        }
      } else {
        // Consent not active - request consent from React Native
        if (!consentRequestedRef.current) {
          consentRequestedRef.current = true;

          // Send message to React Native if available
          if (window.ReactNativeWebView?.postMessage) {
            window.ReactNativeWebView.postMessage(
              JSON.stringify({
                type: 'CONSENT_REQUIRED',
                action: 'requestConsent',
              }),
            );
          }

          setWaitingForConsent(true);
        }
      }
    },
    onError: () => {
      // Preserve the redirect URL so the error screen's "Return to App" can deep-link back.
      const redirectUrlFromParams = searchParams.get('redirectUrl');
      const errorParams = new URLSearchParams({ code: 'CONSENT_CHECK_FAILED' });
      if (redirectUrlFromParams) {
        errorParams.set('redirectUrl', redirectUrlFromParams);
      }
      navigate(`/error?${errorParams.toString()}`, { replace: true });
    },
  });

  // Listen for consent granted from React Native
  useEffect(() => {
    if (!waitingForConsent) return;

    const checkConsentInterval = setInterval(() => {
      if (window.__consentGranted === true && !chatInitializedRef.current) {
        chatInitializedRef.current = true;
        setWaitingForConsent(false);

        // Store redirect URL if available
        const redirectUrlFromParams = searchParams.get('redirectUrl');
        if (redirectUrlFromParams) {
          setRedirectUrl(redirectUrlFromParams);
        }

        setConsentChecked(true);
      }
    }, 100);

    return () => clearInterval(checkConsentInterval);
  }, [waitingForConsent, searchParams, setRedirectUrl]);

  // A single loading screen covers both parallel API calls (session + content
  // config) as well as the wait for external consent.
  if (isConsentLoading || isConfigLoading || waitingForConsent) {
    return <ChatLoadingScreen />;
  }

  // Both requests resolved and consent is active: show the chat.
  if (consentChecked) {
    return (
      <Container aria-label="Optumus chat">
        {showWelcomeBanner ? <ChatWelcomeBanner /> : null}
        <ChatPanel>
          <ChatWidget initialPrompt={initialPrompt} botIcon={OptumusLogo} />
        </ChatPanel>
      </Container>
    );
  }

  // Fallback while state settles.
  return (
    <div
      style={{ height: '100dvh', minHeight: '100dvh', width: '100%' }}
      data-testid="chat-loading-screen"
    >
      <ChatLoadingScreen />
    </div>
  );
};

// Full-screen shell: the chat fills the entire viewport and is responsive to any
// screen size (uses dvh so mobile browser chrome doesn't clip the layout).
const Container = styled('div', {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  height: 'var(--app-height, 100dvh)',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: '$core.color.neutral.0',
  // Follow the visual viewport when WebKit scrolls the page under the keyboard,
  // so the footer stays flush above the keyboard instead of drifting to the top.
  transform: 'translateY(var(--app-offset-top, 0px))',
});

const ChatPanel = styled('main', {
  flex: 1,
  minHeight: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  padding: '$core.spacing.200',
  boxSizing: 'border-box',
  backgroundColor: '$core.color.neutral.0',
});
